import React from "react";
import * as yup from "yup";
import { Formik } from "formik";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import { CropSquare } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { useSelector } from "react-redux";
import { selectSelectedCollection } from "../store/collection/collection.selector";
import { getVectorFields } from "../helper/collection.helper";
import { CollectionService } from "../services";

interface IProps {
  open: boolean;
  onClose: () => void;
}

export interface IAdvanceClusterFacetFormValue {
  vectorField: string;
  facetsFields?: string;
  pageSize: number;
  page: number;
  alias: string;
  asc: boolean;
  dateInterval: string;
}

const initialForm: IAdvanceClusterFacetFormValue = {
  vectorField: "",
  facetsFields: "",
  alias: "default",
  pageSize: 20,
  page: 1,
  asc: true,
  dateInterval: "monthly",
};

const clusterFacetValidationSchema = yup
  .object()
  .shape<IAdvanceClusterFacetFormValue>({
    vectorField: yup.string().required("Vector field is required"),
    facetsFields: yup.string(),
    asc: yup.boolean().required(),
    alias: yup.string().required("Alias is required"),
    dateInterval: yup.string().required("Date interval is required"),
    page: yup.number().required("Page number is required"),
    pageSize: yup.number().required("Page size required"),
  });

const AdvanceClusterFacetModal: React.FC<IProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const selectedCollection = useSelector(selectSelectedCollection);
  const vectorFields = getVectorFields(selectedCollection.data?.schema || {});

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.secondary,
        }}
      >
        Get cluster facets
      </DialogTitle>

      <Formik
        validationSchema={clusterFacetValidationSchema}
        initialValues={{ ...initialForm }}
        onSubmit={async (values: IAdvanceClusterFacetFormValue) => {
          CollectionService.getAdvanceClusteringFacets(values);
          onClose();
        }}
      >
        {({
          handleSubmit,
          values,
          setValues,
          handleChange,
          handleBlur,
          touched,
          errors,
        }) => (
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <DialogContent>
              <Autocomplete
                options={vectorFields}
                value={values.vectorField}
                style={{ marginBottom: theme.spacing(2) }}
                fullWidth
                onChange={(_, value) =>
                  setValues({ ...values, vectorField: value || "" })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    placeholder="Vector field"
                    color="primary"
                    variant="outlined"
                  />
                )}
              />
              <TextField
                fullWidth
                style={{ marginBottom: theme.spacing(2) }}
                variant="outlined"
                placeholder="Facet fields (seperate with comma)"
                name="facetsFields"
                value={values.facetsFields}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.facetsFields && errors.facetsFields}
                error={touched.facetsFields && Boolean(errors.facetsFields)}
              />

              <Collapse in={Boolean(values.facetsFields?.length)} unmountOnExit>
                <Box display="flex" alignItems="center" flexWrap="wrap" mb={2}>
                  {values.facetsFields
                    ?.split(",")
                    .filter((x) => x)
                    .map((element, index) => (
                      <Box
                        p={1}
                        bgcolor={theme.palette.grey[200]}
                        key={index}
                        mr={1}
                        mb={1}
                        borderRadius={4}
                      >
                        <Typography>{element}</Typography>
                      </Box>
                    ))}
                </Box>
              </Collapse>
              <TextField
                fullWidth
                style={{ marginBottom: theme.spacing(2) }}
                variant="outlined"
                placeholder="Alias is used to name a cluster"
                name="alias"
                value={values.alias}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.alias && errors.alias}
                error={touched.alias && Boolean(errors.alias)}
              />
              <TextField
                fullWidth
                style={{ marginBottom: theme.spacing(2) }}
                variant="outlined"
                placeholder="Date interval"
                name="dateInterval"
                value={values.dateInterval}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.dateInterval && errors.dateInterval}
                error={touched.dateInterval && Boolean(errors.dateInterval)}
              />
              <TextField
                fullWidth
                style={{ marginBottom: theme.spacing(2) }}
                type="number"
                variant="outlined"
                placeholder="Page size"
                name="pageSize"
                value={values.pageSize}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.pageSize && errors.pageSize}
                error={touched.pageSize && Boolean(errors.pageSize)}
              />
              <TextField
                fullWidth
                style={{ marginBottom: theme.spacing(2) }}
                type="number"
                variant="outlined"
                placeholder="Page number"
                name="page"
                value={values.page}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.page && errors.page}
                error={touched.page && Boolean(errors.page)}
              />

              <FormControlLabel
                label="Sort result by ascending"
                labelPlacement="end"
                control={
                  <Checkbox
                    icon={<CropSquare color="primary" />}
                    checked={values.asc}
                    onChange={handleChange}
                    name="asc"
                    color="primary"
                    disableRipple
                  />
                }
              />
            </DialogContent>

            <Divider />
            <DialogActions>
              <Button
                style={{
                  textTransform: "none",
                  borderColor: theme.palette.error.light,
                  color: theme.palette.error.light,
                }}
                variant="outlined"
                disableRipple
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                color="primary"
                type="submit"
                style={{ textTransform: "none" }}
                variant="outlined"
                disableRipple
              >
                Get facets
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AdvanceClusterFacetModal;
